import type { GameStore } from '../../store/GameStore';

interface LogEntry {
  message: string;
  type: 'info' | 'success' | 'warning' | 'urgent';
  year: number;
  week: number;
}

/**
 * Persistent notification log — stores all events and shows a scrollable history.
 * Click the bell icon in the HUD to toggle.
 */
export class NotificationLog {
  private element: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private countBadge: HTMLSpanElement;
  private store: GameStore;
  private visible: boolean = false;
  private entries: LogEntry[] = [];
  private unreadCount: number = 0;
  private unsubscribers: (() => void)[] = [];

  constructor(parent: HTMLElement, store: GameStore) {
    this.store = store;

    // Toggle button (mounted separately in the HUD area)
    this.countBadge = document.createElement('span');
    this.countBadge.style.cssText = 'display:none;position:absolute;top:-4px;right:-4px;background:var(--color-danger);color:#fff;font-size:8px;padding:1px 4px;border-radius:8px;font-weight:700;';

    // Panel
    this.element = document.createElement('div');
    this.element.className = 'panel';
    this.element.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;width:280px;transform:translateX(100%);transition:transform var(--transition-normal);pointer-events:auto;z-index:10;';

    const header = document.createElement('div');
    header.className = 'panel__header';
    const title = document.createElement('span');
    title.textContent = 'Event Log';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.element.appendChild(header);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel__body';
    this.bodyEl.style.cssText = 'display:flex;flex-direction:column;gap:2px;';
    this.element.appendChild(this.bodyEl);

    parent.appendChild(this.element);

    // Subscribe to game events
    this.unsubscribers.push(
      store.on('STAFF_LEVELED_UP', (e) => { if (e.type === 'STAFF_LEVELED_UP') this.add(`${e.name} leveled up to Lv.${e.newLevel}`, 'success'); }),
      store.on('PROJECT_COMPLETED', (e) => { if (e.type === 'PROJECT_COMPLETED') this.add(`${e.name} ready for release`, 'info'); }),
      store.on('REVIEW_SCORES_GENERATED', (e) => { if (e.type === 'REVIEW_SCORES_GENERATED') this.add(`${e.name}: ${e.average.toFixed(1)}/10${e.isHallOfFame ? ' — Hall of Fame!' : ''}`, e.isHallOfFame ? 'success' : 'info'); }),
      store.on('STAFF_DEMAND', (e) => { if (e.type === 'STAFF_DEMAND') this.add(`${e.name} has a request`, 'warning'); }),
      store.on('STAFF_LEFT', (e) => { if (e.type === 'STAFF_LEFT') this.add(`${e.name} left the company`, 'urgent'); }),
      store.on('LOW_FUNDS_WARNING', (e) => { if (e.type === 'LOW_FUNDS_WARNING') this.add(`Low funds! ${e.weeksOfRunway} weeks runway`, 'urgent'); }),
      store.on('UNLOCK_ACHIEVED', (e) => { if (e.type === 'UNLOCK_ACHIEVED') this.add(e.description, 'success'); }),
      store.on('COMPANY_LEVELED_UP', (e) => { if (e.type === 'COMPANY_LEVELED_UP') this.add(`Company reached Level ${e.newLevel}`, 'success'); }),
      store.on('CONTRACT_COMPLETED', (e) => { if (e.type === 'CONTRACT_COMPLETED') this.add(`Contract done: +$${e.reward}`, 'success'); }),
      store.on('STAFF_HIRED', (e) => { if (e.type === 'STAFF_HIRED') this.add(`Hired ${e.name} (${e.role})`, 'info'); }),
      store.on('OFFICE_UPGRADED', (e) => { if (e.type === 'OFFICE_UPGRADED') this.add(`Office upgraded: ${e.newName}`, 'success'); }),
      store.on('RANDOM_EVENT', (e) => { if (e.type === 'RANDOM_EVENT') this.add(e.title, 'warning'); }),
      store.on('SCRIPTED_EVENT', (e) => { if (e.type === 'SCRIPTED_EVENT') this.add(e.title, 'success'); }),
      store.on('PLATFORM_LAUNCHED', (e) => { if (e.type === 'PLATFORM_LAUNCHED') this.add(`New platform: ${e.platformName}`, 'success'); }),
      store.on('PLATFORM_RETIRED', (e) => { if (e.type === 'PLATFORM_RETIRED') this.add(`${e.platformName} discontinued`, 'warning'); }),
      store.on('ACHIEVEMENT_UNLOCKED', (e) => { if (e.type === 'ACHIEVEMENT_UNLOCKED') this.add(`${e.icon} ${e.name}`, 'success'); }),
      store.on('CANDIDATES_READY', (e) => { if (e.type === 'CANDIDATES_READY') this.add(`${e.tier} found ${e.count} candidates`, 'info'); }),
      store.on('GAME_OVER', () => this.add('GAME OVER', 'urgent')),
    );
  }

  /** Get the badge element to mount in the HUD. */
  get badge(): HTMLSpanElement {
    return this.countBadge;
  }

  add(message: string, type: LogEntry['type']): void {
    const state = this.store.getState();
    this.entries.push({ message, type, year: state.company.currentYear, week: state.company.currentWeek });
    if (this.entries.length > 200) this.entries.shift();
    if (!this.visible) {
      this.unreadCount++;
      this.countBadge.textContent = String(this.unreadCount);
      this.countBadge.style.display = '';
    } else {
      this.render();
    }
  }

  show(): void {
    this.visible = true;
    this.unreadCount = 0;
    this.countBadge.style.display = 'none';
    this.element.style.transform = 'translateX(0)';
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.element.style.transform = 'translateX(100%)';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  get isVisible(): boolean { return this.visible; }

  render(): void {
    if (!this.visible) return;
    this.bodyEl.innerHTML = '';

    const typeColors: Record<string, string> = {
      info: 'var(--color-text-secondary)', success: 'var(--color-success)',
      warning: 'var(--color-warning)', urgent: 'var(--color-danger)',
    };

    // Show newest first
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i]!;
      const el = document.createElement('div');
      el.style.cssText = `font-size:10px;padding:3px 0;border-bottom:1px solid var(--border-color);display:flex;gap:6px;`;

      const time = document.createElement('span');
      time.textContent = `Y${entry.year}W${entry.week}`;
      time.style.cssText = 'color:var(--color-text-muted);flex-shrink:0;width:42px;';

      const msg = document.createElement('span');
      msg.textContent = entry.message;
      msg.style.color = typeColors[entry.type] ?? 'var(--color-text-primary)';

      el.appendChild(time);
      el.appendChild(msg);
      this.bodyEl.appendChild(el);
    }

    if (this.entries.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'Events will appear here as they happen — releases, hires, achievements, and more.';
      empty.style.cssText = 'font-size:11px;color:var(--color-text-muted);text-align:center;padding:24px 16px;line-height:1.5;';
      this.bodyEl.appendChild(empty);
    }
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.element.remove();
  }
}
